import { Button, Typography } from 'omakase';
import { isFinite as _isFinite, filter, sumBy, toInteger, toNumber } from 'es-toolkit/compat';
import { core } from '@tock/coretypes';
import { reduxForm } from 'redux-form';
import Field from '@tock/shared/components/forms/Field';
import GratuityOption from 'components/business/gratuity/GratuityOption';
import PriceInput from '@tock/shared/components/inputs/PriceInput';
import React from 'react';

interface Props {
  deliveryServiceProvider?: core.TicketType['deliveryServiceProvider'];
  extraChargeDetails: core.ExtraChargeDetails;
  purchase: core.PurchaseOrder;
  onGratuityChange(newGratuityRate: number): void;
}

const MAX_GRATUITY_PERCENTAGE = 100;
// DoorDash has a hard limit of $120 on gratuity, as of 11-23-2021
const MAX_DOORDASH_GRATUITY_CENTS = 12000.0;
export const doorDashMaxTipExceededErrorString = `Our delivery partner DoorDash sets a tip amount maximum of $${
  MAX_DOORDASH_GRATUITY_CENTS / 100
}. Please adjust your tip amount to
continue.`;

/**
 * Based on the current custom gratuity value, and the delivery service provider (if applicable), determine if
 * the indicated gratuity violates DoorDash's limit of $120
 */
export const isGratuityViolatingDoordashLimit = (
  customGratuityCents: number,
  deliveryServiceProvider?: core.TicketType['deliveryServiceProvider']
) => deliveryServiceProvider === 'DOORDASH' && customGratuityCents > MAX_DOORDASH_GRATUITY_CENTS;

interface FormFields {
  customGratuityCents: number;
}

const validate = (values: FormFields, props: Props) => {
  const errors: ReduxFormErrors<FormFields> = {};
  const { deliveryServiceProvider, purchase } = props;
  const baseAmount = getBaseAmount(purchase);
  if (values.customGratuityCents > (baseAmount * MAX_GRATUITY_PERCENTAGE) / 100.0) {
    errors.customGratuityCents = `Gratuity cannot be greater than ${MAX_GRATUITY_PERCENTAGE}%`;
  }
  if (isGratuityViolatingDoordashLimit(values.customGratuityCents, deliveryServiceProvider)) {
    errors.customGratuityCents = doorDashMaxTipExceededErrorString;
  }
  if (values.customGratuityCents < 0) {
    errors.customGratuityCents = 'Gratuity cannot be negative';
  }
  if (!_isFinite(toNumber(values.customGratuityCents))) {
    errors.customGratuityCents = 'Gratuity must be numeric';
  }
  return errors;
};

function getBaseAmount(purchase: core.PurchaseOrder) {
  const subtotalLessComp = purchase.subtotal - (purchase.compAmount || 0);
  const postSubtotalDiscount = sumBy(
    filter(purchase.discount, (discount) => discount.discountType === 'SUBTOTAL_COMP'),
    (discount) => discount.amountCents ?? 0
  );
  return subtotalLessComp - postSubtotalDiscount;
}

const GratuityInput: React.FunctionComponent<Props & ReduxFormProps<FormFields>> = ({
  extraChargeDetails: { gratuityOptionOne, gratuityOptionTwo, gratuityOptionThree },
  deliveryServiceProvider,
  fields: { customGratuityCents },
  onGratuityChange,
  purchase,
}) => {
  const [showCustomGratuity, setShowCustomGratuity] = React.useState(false);

  React.useEffect(() => {
    if (
      isGratuityViolatingDoordashLimit(customGratuityCents.value, deliveryServiceProvider) &&
      !showCustomGratuity
    ) {
      setShowCustomGratuity(true);
    }
  }, [customGratuityCents, deliveryServiceProvider, purchase, showCustomGratuity]);

  const baseAmount = getBaseAmount(purchase);

  const handleCustomGratuity = () => {
    setShowCustomGratuity(true);
  };

  const handleChangeCustomGratuity = (event: React.FocusEvent<HTMLInputElement>) => {
    const baseAmount = getBaseAmount(purchase);
    const newGratuityAmount = toNumber(event.target.value);
    if (_isFinite(newGratuityAmount)) {
      handleGratuityChange((newGratuityAmount * 10000) / baseAmount);
    }
  };

  const handleGratuityChange = (rate: number) => {
    if (onGratuityChange) {
      onGratuityChange(Math.max(Math.min(MAX_GRATUITY_PERCENTAGE, rate), 0));
    }
  };

  const handleGratuityZero = () => {
    handleGratuityChange(0);
  };

  const handleGratuityOne = () => {
    handleGratuityChange(toInteger(gratuityOptionOne));
  };

  const handleGratuityTwo = () => {
    handleGratuityChange(toInteger(gratuityOptionTwo));
  };

  const handleGratuityThree = () => {
    handleGratuityChange(toInteger(gratuityOptionThree));
  };

  return (
    <div className="Receipt-gratuity">
      <Typography component="h5" variant="body02Strong" id="grauity-label">
        Add gratuity (optional)
      </Typography>
      <div role="group" aria-labelledby="grauity-label" className="Receipt-gratuityButtons">
        <GratuityOption
          gratuityOption={gratuityOptionOne}
          selectedGratuityRate={purchase.selectedGratuityRate}
          baseAmount={baseAmount}
          onClick={handleGratuityOne}
          data-testid="gratuity-button-option1"
        />
        <GratuityOption
          gratuityOption={gratuityOptionTwo}
          selectedGratuityRate={purchase.selectedGratuityRate}
          baseAmount={baseAmount}
          onClick={handleGratuityTwo}
          data-testid="gratuity-button-option2"
        />
        <GratuityOption
          gratuityOption={gratuityOptionThree}
          selectedGratuityRate={purchase.selectedGratuityRate}
          baseAmount={baseAmount}
          onClick={handleGratuityThree}
          data-testid="gratuity-button-option3"
        />
        <GratuityOption
          gratuityOption={0}
          selectedGratuityRate={purchase.selectedGratuityRate}
          baseAmount={baseAmount}
          onClick={handleGratuityZero}
          data-testid="gratuity-button-zero"
        />
      </div>

      {!showCustomGratuity && (
        <Button
          variant="ghost"
          size="medium"
          onClick={handleCustomGratuity}
          data-testid="gratuity-custom-link"
        >
          Custom amount
        </Button>
      )}

      {showCustomGratuity && (
        <>
          <div className="Receipt-gratuityCustomInput">
            <Field
              field={customGratuityCents}
              warn={
                (100.0 * customGratuityCents.value) / baseAmount > 50
                  ? 'You are entering a gratuity larger than 50%'
                  : undefined
              }
              error={
                isGratuityViolatingDoordashLimit(customGratuityCents.value, deliveryServiceProvider)
                  ? doorDashMaxTipExceededErrorString
                  : undefined
              }
              data-testid="custom-gratuity-field"
              label="Custom gratuity amount"
              labelFor="customGratuityCents"
            >
              <PriceInput
                placeholder="Gratuity"
                id="customGratuityCents"
                field={customGratuityCents}
                onBlur={handleChangeCustomGratuity}
                data-testid="custom-gratuity-input"
              />
            </Field>
          </div>
        </>
      )}
    </div>
  );
};

export default reduxForm<Props, FormFields>(
  {
    form: 'GratuityForm',
    fields: ['customGratuityCents'],
    validate,
  },
  (state, props: Props) => {
    return {
      initialValues: {
        customGratuityCents: props.purchase.gratuityCents,
      },
    };
  }
)(GratuityInput);
